import React, { FC, ReactNode, useCallback, useState } from 'react';
import { Box, CircularProgress, IconButton, Modal, SxProps, Theme } from '@mui/material';
import { CloseRounded } from '@mui/icons-material';
import { warningModalStyles } from './warning-modal.styles';
import { clsx, useStyles } from '../../utils';
import { Showable } from '../utils';
import { CoreButtonBase } from '../_core';

export interface WarningModalProps {
	open: boolean;
	text: string | ReactNode;
	title?: string;
	submitButtonText?: string;
	rootSx?: SxProps<Theme>;
	titleSx?: SxProps<Theme>;
	textSx?: SxProps<Theme>;
	disableClose?: boolean;
	onClose?(): void;
	onSubmit?(): Promise<void> | void;
}

export const WarningModal: FC<WarningModalProps> = props => {
	const {
		open,
		title,
		text,
		submitButtonText,
		rootSx,
		titleSx,
		textSx,
		disableClose,
		onClose,
		onSubmit,
	} = props;
	const [isSubmitting, setIsSubmitting] = useState(false);
	const styles = useStyles(warningModalStyles);

	const handleClose = useCallback(() => {
		if (!disableClose) {
			onClose?.();
		}
	}, [disableClose, onClose])

	const handleSubmit = useCallback(async (): Promise<void> => {
		setIsSubmitting(true);

		try {
			await onSubmit?.();
			handleClose();
		} finally {
			setIsSubmitting(false);
		}
	}, [handleClose, onSubmit]);

	return (
		<Modal id="warning-modal" open={open} onClose={handleClose} sx={styles.modal}>
			<Box sx={clsx(styles.root, rootSx)}>
				<Showable hide={disableClose}>
					<IconButton onClick={handleClose} sx={styles.closeButton}>
						<CloseRounded />
					</IconButton>
				</Showable>
				<Box display="flex" flexDirection="column" gap="12px">
					<Showable show={title}>
						<Box sx={clsx(styles.title, titleSx)}>{title}</Box>
					</Showable>
					<Box sx={clsx(styles.text, textSx)}>{text}</Box>
				</Box>
				<Box display="flex" justifyContent="center">
					<CoreButtonBase
						sx={styles.submitButton}
						disabled={isSubmitting}
						onClick={handleSubmit}
						className={isSubmitting ? "core-button-loading" : undefined}
					>
						<Showable show={isSubmitting}>
							<CircularProgress size={16} />
						</Showable>
						{submitButtonText || 'Go to event page'}
					</CoreButtonBase>
				</Box>
			</Box>
		</Modal>
	);
};
